import React, { useState, Component } from 'react';
import { XIcon, ChevronLeftIcon, ChevronRightIcon, MapPinIcon, FacebookIcon, TwitterIcon, InstagramIcon, MailIcon, MessageCircleIcon, VideoIcon } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
// Fix for default marker icon in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});
interface StoryPopupProps {
  onClose: () => void;
}
export function StoryPopup({
  onClose
}: StoryPopupProps) {
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState(0);
  const [showSharePopup, setShowSharePopup] = useState(false);
  // Ashridge Estate coordinates (National Trust woodland known for dormice)
  const location = {
    lat: 51.8167,
    lng: -0.5833,
    name: 'Ashridge Estate'
  };
  const storyData = {
    title: 'Wildlife Discovery at Ashridge Estate',
    description: 'A rare dormouse nest was found during habitat restoration, thanks to community contributions.',
    charity: 'National Trust'
  };
  const handleSeeProgress = () => {
    onClose();
    navigate('/habitat-details');
  };
  const handleShare = (platform: string) => {
    const url = window.location.href;
    const text = `${storyData.title} - ${storyData.description}`;
    let shareUrl = '';
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'x':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
        break;
      case 'instagram':
        // Copy to clipboard and open Instagram
        navigator.clipboard.writeText(`${text}\n\n${url}`);
        window.open('https://www.instagram.com/', '_blank');
        setShowSharePopup(false);
        return;
      case 'tiktok':
        // TikTok doesn't support direct URL sharing, so copy to clipboard
        navigator.clipboard.writeText(`${text}\n\n${url}`);
        alert('Link copied to clipboard! You can now share it on TikTok.');
        setShowSharePopup(false);
        return;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=${encodeURIComponent(storyData.title)}&body=${encodeURIComponent(text + '\n\n' + url)}`;
        break;
    }
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
      setShowSharePopup(false);
    }
  };
  const screens = [{
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/tmJP9ksBseLYFntHR5GtW9/ChatGPT_Image_Nov_10%2C_2025_at_04_24_36_PM.png',
    content: <div className="flex items-center justify-center h-full" data-id="element-700">
          <h1 className="text-5xl font-bold text-white text-center px-8" data-id="element-701">
            Found today.
          </h1>
        </div>
  }, {
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/tmJP9ksBseLYFntHR5GtW9/ChatGPT_Image_Nov_10%2C_2025_at_04_24_36_PM.png',
    content: <div className="flex flex-col justify-end h-full p-8 pb-24" data-id="element-702">
          <p className="text-white text-xl mb-6 leading-relaxed" data-id="element-703">
            A rare dormouse nest was spotted during habitat restoration.
          </p>
          <div className="mb-6 rounded-xl overflow-hidden shadow-lg" data-id="element-704">
            <MapContainer center={[location.lat, location.lng]} zoom={13} style={{
          height: '200px',
          width: '100%'
        }} scrollWheelZoom={false} dragging={false} zoomControl={false} data-id="element-705">
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' data-id="element-706" />
              <Marker position={[location.lat, location.lng]} data-id="element-707">
                <Popup data-id="element-708">{location.name}</Popup>
              </Marker>
            </MapContainer>
          </div>
          <div className="flex items-center text-white mb-4" data-id="element-709">
            <MapPinIcon className="w-5 h-5 mr-2" data-id="element-710" />
            <span className="text-lg" data-id="element-711">{location.name}, Hertfordshire</span>
          </div>
          <p className="text-white/80 text-base italic" data-id="element-712">
            Funded by community contributions
          </p>
        </div>
  }, {
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/tmJP9ksBseLYFntHR5GtW9/ChatGPT_Image_Nov_10%2C_2025_at_04_24_36_PM.png',
    content: <div className="flex flex-col justify-end h-full p-8 pb-24" data-id="element-713">
          <h2 className="text-4xl font-bold text-white mb-8" data-id="element-714">
            You helped protect this habitat.
          </h2>
          <div className="space-y-3" data-id="element-715">
            <button onClick={handleSeeProgress} className="w-full bg-white text-primary py-4 rounded-full font-semibold text-lg hover:bg-gray-100 transition-colors" data-id="element-716">
              See progress
            </button>
            <button onClick={() => setShowSharePopup(true)} className="w-full bg-white/20 backdrop-blur-sm text-white py-4 rounded-full font-semibold text-lg hover:bg-white/30 transition-colors" data-id="element-717">
              Share
            </button>
          </div>
        </div>
  }];
  const nextScreen = () => {
    if (currentScreen < screens.length - 1) {
      setCurrentScreen(currentScreen + 1);
    }
  };
  const prevScreen = () => {
    if (currentScreen > 0) {
      setCurrentScreen(currentScreen - 1);
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90" data-id="element-718">
      <div className="relative w-full max-w-md h-[80vh] bg-black rounded-2xl overflow-hidden shadow-2xl" data-id="element-719">
        {/* Progress bars */}
        <div className="absolute top-0 left-0 right-0 z-20 flex gap-1 p-3" data-id="element-720">
          {screens.map((_, index) => <div key={index} className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden" data-id="element-721">
              <div className={`h-full bg-white transition-all duration-300 ${index < currentScreen ? 'w-full' : index === currentScreen ? 'w-full' : 'w-0'}`} data-id="element-722" />
            </div>)}
        </div>
        {/* Close button */}
        <button onClick={onClose} className="absolute top-4 right-4 z-20 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors" data-id="element-723">
          <XIcon className="w-6 h-6" data-id="element-724" />
        </button>
        {/* National Trust badge */}
        <div className="absolute top-16 left-4 z-20 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full" data-id="element-725">
          <p className="text-sm font-semibold text-gray-800" data-id="element-726">National Trust</p>
        </div>
        {/* Background image */}
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.6)), url(${screens[currentScreen].image})`
      }} data-id="element-727" />
        {/* Content */}
        <div className="relative h-full" data-id="element-728">{screens[currentScreen].content}</div>
        {/* Navigation areas */}
        {currentScreen > 0 && <button onClick={prevScreen} className="absolute left-0 top-0 bottom-0 w-1/3 z-10 flex items-center justify-start pl-4" data-id="element-729">
            <div className="p-2 rounded-full bg-black/30 hover:bg-black/50 transition-colors" data-id="element-730">
              <ChevronLeftIcon className="w-6 h-6 text-white" data-id="element-731" />
            </div>
          </button>}
        {currentScreen < screens.length - 1 && <button onClick={nextScreen} className="absolute right-0 top-0 bottom-0 w-1/3 z-10 flex items-center justify-end pr-4" data-id="element-732">
            <div className="p-2 rounded-full bg-black/30 hover:bg-black/50 transition-colors" data-id="element-733">
              <ChevronRightIcon className="w-6 h-6 text-white" data-id="element-734" />
            </div>
          </button>}
        {/* Share popup */}
        {showSharePopup && <>
            <div className="absolute inset-0 z-30 bg-black/50" onClick={() => setShowSharePopup(false)} data-id="element-735" />
            <div className="absolute bottom-32 left-1/2 transform -translate-x-1/2 z-40 bg-white rounded-2xl shadow-2xl p-6 w-80" data-id="element-736">
              <div className="flex items-center justify-between mb-4" data-id="element-737">
                <h3 className="font-semibold text-gray-800 text-lg" data-id="element-738">
                  Share this story
                </h3>
                <button onClick={() => setShowSharePopup(false)} className="text-gray-400 hover:text-gray-600" data-id="element-739">
                  <XIcon className="w-5 h-5" data-id="element-740" />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-4" data-id="element-741">
                <button onClick={() => handleShare('x')} className="flex flex-col items-center justify-center p-3 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-742">
                  <div className="w-12 h-12 rounded-full bg-black flex items-center justify-center mb-2" data-id="element-743">
                    <TwitterIcon className="w-6 h-6 text-white" data-id="element-744" />
                  </div>
                  <span className="text-xs text-gray-600" data-id="element-745">X</span>
                </button>
                <button onClick={() => handleShare('facebook')} className="flex flex-col items-center justify-center p-3 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-746">
                  <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center mb-2" data-id="element-747">
                    <FacebookIcon className="w-6 h-6 text-white" data-id="element-748" />
                  </div>
                  <span className="text-xs text-gray-600" data-id="element-749">Facebook</span>
                </button>
                <button onClick={() => handleShare('instagram')} className="flex flex-col items-center justify-center p-3 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-750">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 flex items-center justify-center mb-2" data-id="element-751">
                    <InstagramIcon className="w-6 h-6 text-white" data-id="element-752" />
                  </div>
                  <span className="text-xs text-gray-600" data-id="element-753">Instagram</span>
                </button>
                <button onClick={() => handleShare('tiktok')} className="flex flex-col items-center justify-center p-3 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-754">
                  <div className="w-12 h-12 rounded-full bg-black flex items-center justify-center mb-2" data-id="element-755">
                    <VideoIcon className="w-6 h-6 text-white" data-id="element-756" />
                  </div>
                  <span className="text-xs text-gray-600" data-id="element-757">TikTok</span>
                </button>
                <button onClick={() => handleShare('whatsapp')} className="flex flex-col items-center justify-center p-3 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-758">
                  <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center mb-2" data-id="element-759">
                    <MessageCircleIcon className="w-6 h-6 text-white" data-id="element-760" />
                  </div>
                  <span className="text-xs text-gray-600" data-id="element-761">WhatsApp</span>
                </button>
                <button onClick={() => handleShare('email')} className="flex flex-col items-center justify-center p-3 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-762">
                  <div className="w-12 h-12 rounded-full bg-gray-600 flex items-center justify-center mb-2" data-id="element-763">
                    <MailIcon className="w-6 h-6 text-white" data-id="element-764" />
                  </div>
                  <span className="text-xs text-gray-600" data-id="element-765">Email</span>
                </button>
              </div>
            </div>
          </>}
      </div>
    </div>;
}